// Shared types for Kindred Live QA Review (backend + webapp)

// Re-export enums that both sides need
export enum KindredPublishStatus {
  Draft = 0,
  AwaitingApproval = 1,
  Approved = 2,
  Deactivated = 3,
}

export enum DomainSearchMode {
  CouponDomains = "coupon_domains",
  CampaignDomains = "campaign_domains",
  Both = "both",
}

// ── Pagination ──────────────────────────────────────────────

export interface IKindredPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ── Deals (Campaigns) ──────────────────────────────────────

export interface IKindredDealSummary {
  id: string;
  name: string;
  brandId: string;
  brandName: string;
  /** The network's advertiser/merchant id for this campaign. */
  merchantId: string | null;
  source: string;
  publishedStatus: number;
  suspended: boolean;
  featured: boolean;
  featuredLevel2: boolean;
  exclusive: boolean;
  cashbackType: string;
  totalCommission: number | null;
  startDate: string;
  endDate: string;
  isActive: boolean;
  countries: string[];
  domains: string[];
  isTopBrand: boolean;
  shopLink: string;
}

export interface IKindredDealDetail extends IKindredDealSummary {
  description: string;
  images: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  overridesFlag: number;
  suspendedAt: string | null;
  suspendedReason: string | null;
  merchantFixedCashback: number | null;
  domainDetails: IKindredDealDomain[];
}

export interface IKindredDealDomain {
  id: string;
  domain: string;
  brandId: string;
  validated: boolean;
}

export interface IKindredDealsResponse {
  results: IKindredDealSummary[];
  pagination: IKindredPagination;
}

export interface IKindredDealsFilter {
  publishedStatus?: number;
  suspended?: boolean;
  source?: string | string[];
  countryCode?: string | string[];
  cashbackType?: string;
  domain?: string;
  featured?: boolean;
  topBrands?: boolean;
  brandId?: string | string[];
  campaignId?: string;
  merchantId?: string;
  keywords?: string;
  activeDomainsOnly?: boolean;
  active?: boolean;
}

// ── Coupons ─────────────────────────────────────────────────

export interface IKindredCouponSummary {
  id: string;
  code: string;
  brandId: string;
  brandName: string;
  discount: number;
  summary: string | null;
  validFrom: string;
  validTo: string;
  source: string;
  publishedStatus: number;
  priority: number;
  coverage: number;
  countries: string[];
  couponDomains: string[];
  campaignDomains: string[];
  isTopBrand: boolean;
  deactivatedAt: string | null;
}

export interface IKindredCouponDetail extends IKindredCouponSummary {
  createdAt: string;
  updatedAt: string;
  lastTestedAt: string | null;
  overridesFlag: number;
  couponCodeReviewFlags: number;
  hasProhibitedContents: number;
  isRoutineProhibitedContentChecked: boolean;
  couponHash: string;
  reason: string;
  expiryDate: string | null;
  countryDetails: IKindredCouponCountry[];
  couponDomainDetails: string[];
  campaignDomainDetails: IKindredCouponCampaignDomain[];
  metadata: IKindredCouponMetadata | null;
}

export interface IKindredCouponCountry {
  countryCode: string;
  isEnabled: boolean;
}

export interface IKindredCouponCampaignDomain {
  domain: string;
  campaignId: string;
  campaignName: string | null;
  campaignSource: string | null;
  brandId: string | null;
  brandName: string | null;
  validated: boolean;
}

export interface IKindredCouponsResponse {
  results: IKindredCouponSummary[];
  pagination: IKindredPagination;
}

export interface IKindredCouponsFilter {
  couponCode?: string;
  brandId?: string | string[];
  topBrands?: boolean;
  countryCode?: string | string[];
  publishedStatus?: number | number[];
  source?: string | string[];
  domain?: string;
  domainSearchMode?: DomainSearchMode;
  validatedCampaignDomainsOnly?: boolean;
  active?: boolean;
  coverage?: number;
}

export enum KindredCouponCoverage {
  None = 0,
  SiteWide = 1,
  Category = 2,
  Product = 4,
  Manufacturer = 8,
}

export enum DiscountApplication {
  All = 0,
  Cheapest = 1,
  MostExpensive = 2,
}

export interface IBundleRequirement {
  canonicalKey: string;
  minimumQuantity?: number | null;
  minimumSpend?: number | null;
}

export interface ITargetingConditions {
  minimumSpend?: number | null;
  minimumLinePrice?: number | null;
  minimumQuantity?: number | null;
  maxQuantity?: number | null;
  application?: DiscountApplication | null;
  discountAppliesToCount?: number | null;
  requiresBundleWith?: IBundleRequirement[] | null;
  isActive?: boolean | null;
}

// ── Offers ──────────────────────────────────────────────────

export enum OfferDomainSearchMode {
  OfferDomains = "offer_domains",
  CampaignDomains = "campaign_domains",
  Both = "both",
}

export interface IKindredOfferSummary {
  id: string;
  summary: string | null;
  brandId: string;
  brandName: string;
  validFrom: string;
  validTo: string;
  source: string;
  publishedStatus: number;
  priority: number;
  countries: string[];
  offerDomains: string[];
  campaignDomains: string[];
  isTopBrand: boolean;
  deactivatedAt: string | null;
  trackingLink: string | null;
  promoLink: string | null;
}

export interface IKindredOfferDetail extends IKindredOfferSummary {
  createdAt: string;
  updatedAt: string;
  lastTestedAt: string | null;
  overridesFlag: number;
  hasProhibitedContents: number;
  coverage: number;
  offerHash: string;
  reason: string | null;
  expiryDate: string | null;
  countryDetails: IKindredOfferCountry[];
  offerDomainDetails: string[];
  campaignDomainDetails: IKindredOfferCampaignDomain[];
  metadata: IKindredOfferMetadata | null;
}

export interface IKindredOfferCountry {
  countryCode: string;
  isEnabled: boolean;
}

export interface IKindredOfferCampaignDomain {
  domain: string;
  campaignId: string;
  campaignName: string | null;
  campaignSource: string | null;
  brandId: string | null;
  brandName: string | null;
  validated: boolean;
}

export interface IKindredOffersResponse {
  results: IKindredOfferSummary[];
  pagination: IKindredPagination;
}

export interface IKindredOffersFilter {
  brandId?: string | string[];
  topBrands?: boolean;
  countryCode?: string | string[];
  publishedStatus?: number;
  source?: string | string[];
  domain?: string;
  domainSearchMode?: OfferDomainSearchMode;
  validatedCampaignDomainsOnly?: boolean;
  active?: boolean;
}

// ── Metadata (shared shape for coupons & offers) ────────────

export enum KindredPromoType {
  None = 0,
  Monetary = 1,
  NonMonetary = 2,
}

export enum KindredPromoCoverage {
  None = 0,
  SiteWide = 1,
  Category = 2,
  Product = 4,
}

export enum KindredPromoScope {
  None = 0,
  New = 1,
  Existing = 2,
  All = 3,
}

export enum KindredPromoChannel {
  None = 0,
  Online = 1,
  InStore = 2,
}

export enum KindredDiscountType {
  None = 0,
  Percentage = 1,
  Fixed = 2,
  Others = 3,
}

export const KINDRED_NON_MONETARY_VALUES = [
  "freeShipping",
  "freeGift",
  "freeTrial",
  "freeReturns",
  "loyaltyPoints",
  "buyOneGetOne",
  "extendedWarranty",
  "other",
] as const;

export interface IKindredCouponMetadata {
  promoType: number | null;
  coverage: number | null;
  discountType: number | null;
  discounts: string | null;
  nonMonetaryValue: string | null;
  language: string | null;
  currency: string | null;
  scope: number | null;
  channel: number | null;
  productCategory: string | null;
  rawCategory: string | null;
  isStackable: boolean | null;
  requiresReview: boolean | null;
  reasoning: string | null;
  isReviewed: boolean | null;
  createdAt: string;
  updatedAt: string | null;
  couponHash: string;
}

export interface IKindredOfferMetadata {
  promoType: number | null;
  coverage: number | null;
  discountType: number | null;
  discounts: string | null;
  nonMonetaryValue: string | null;
  language: string | null;
  currency: string | null;
  scope: number | null;
  channel: number | null;
  productCategory: string | null;
  rawCategory: string | null;
  isStackable: boolean | null;
  requiresReview: boolean | null;
  reasoning: string | null;
  isReviewed: boolean | null;
  createdAt: string;
  updatedAt: string | null;
  offerHash: string;
}

// ── Brands ──────────────────────────────────────────────────

export interface IKindredBrandReference {
  id: string;
  name: string;
  logo: string | null;
  countryCode: string | null;
  isTopBrand: boolean;
}

/** Typeahead search result. `total` is the full match count before the page
 *  limit, so the picker can say "showing 20 of 137" rather than looking as
 *  though nothing else matched. */
export interface IKindredBrandSearchResponse {
  results: IKindredBrandReference[];
  total: number;
}

export interface IKindredBrandSummary {
  id: string;
  name: string;
  logo: string | null;
  countryCode: string | null;
  isTopBrand: boolean;
  domainCount: number;
}

export interface IKindredBrandDetail extends IKindredBrandSummary {
  createdAt: string;
  updatedAt: string;
  domains: IKindredBrandDomainInfo[];
  topBrandCountries: string[];
}

export interface IKindredBrandDomainInfo {
  domain: string;
  countryCode: string | null;
  isValidated: boolean;
}

export interface IKindredBrandsResponse {
  results: IKindredBrandSummary[];
  pagination: IKindredPagination;
}

export interface IKindredBrandsFilter {
  name?: string;
  countryCode?: string;
  topBrands?: boolean;
  domain?: string;
}

// ── AI-Validated Brand Domains (staging review) ─────────────

export enum BrandDomainReviewStatus {
  Pending = 0,
  Approved = 1,
}

export interface IAiValidatedBrandDomainDto {
  brandId: string;
  domain: string | null;
  originalBrandName: string | null;
  suggestedBrandName: string | null;
  countryCode: string | null;
  validationReason: string | null;
  reviewStatus: number | null;
}

export interface IAiValidatedBrandDomainResponse {
  results: IAiValidatedBrandDomainDto[];
  pagination: IKindredPagination;
}

export interface IAiValidatedBrandDomainKey {
  brandId: string;
  domain: string;
}

export interface IAiValidatedBrandDomainValidateResult {
  brandId: string;
  domain: string;
  inserted: number;
  reviewed: number;
}

export interface IAiValidatedBrandDomainBatchResult {
  items: IAiValidatedBrandDomainKey[];
  requested: number;
  inserted: number;
  reviewed: number;
}

// ── Audit Trail ─────────────────────────────────────────────

export interface IKindredAuditChange {
  id: string;
  entityId: string;
  entityName: string | null;
  comment: string | null;
  timestamp: string;
  user: string | null;
  system: string | null;
  columnChanges: IKindredAuditColumnChange[];
}

/** A SQL audit row for any entity on a domain, from any app (PowerApp, this one, domain review). */
export interface IKindredDomainAuditChange extends IKindredAuditChange {
  entityType: "coupon" | "deal" | "offer" | "brand" | null;
  /** Coupon code, deal or brand name, or offer summary. */
  entityLabel: string | null;
}

export interface IKindredDomainAuditResponse {
  domain: string;
  /** Coupons, deals, offers and brands searched for this domain. */
  entityCount: number;
  changes: IKindredDomainAuditChange[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface IKindredAuditColumnChange {
  id: string;
  changedColumn: string;
  oldValue: string;
  newValue: string;
}

export interface IKindredAuditResponse {
  changes: IKindredAuditChange[];
  pagination: IKindredPagination;
}

// ── Update DTOs (write operations) ──────────────────────────

export interface IKindredEntityUpdateRequest<T> {
  data: T;
  comment: string;
}

/**
 * A domain submitted against a deal. A bare string leaves an existing domain's
 * `validated` flag untouched; new domains without one default to false.
 */
export interface IKindredDealDomainInput {
  domain: string;
  validated?: boolean;
}

export interface IKindredDealUpdate {
  name?: string;
  brandId?: string;
  publishedStatus?: number;
  suspended?: boolean;
  source?: string;
  cashbackType?: string;
  featured?: boolean;
  featuredLevel2?: boolean;
  exclusive?: boolean;
  description?: string;
  shopLink?: string;
  startDate?: string;
  endDate?: string;
  overridesFlag?: number;
  suspendedReason?: string | null;
  totalCommission?: number | null;
  merchantFixedCashback?: number | null;
  domains?: (string | IKindredDealDomainInput)[];
  countries?: string[];
}

export interface IKindredMetadataUpdate {
  promoType?: number | null;
  coverage?: number | null;
  discountType?: number | null;
  discounts?: string | null;
  nonMonetaryValue?: string | null;
  language?: string | null;
  currency?: string | null;
  scope?: number | null;
  channel?: number | null;
  productCategory?: string | null;
  rawCategory?: string | null;
  isStackable?: boolean | null;
  requiresReview?: boolean | null;
  reasoning?: string | null;
  isReviewed?: boolean | null;
}

export interface IKindredCouponUpdate {
  code?: string;
  brandId?: string;
  publishedStatus?: number;
  reason?: string | null;
  discount?: number;
  summary?: string | null;
  validFrom?: string;
  validTo?: string;
  priority?: number;
  coverage?: number;
  lastTestedAt?: string | null;
  overridesFlag?: number;
  domains?: string[];
  countries?: string[];
  metadata?: IKindredMetadataUpdate;
}

export interface IKindredOfferUpdate {
  brandId?: string;
  publishedStatus?: number;
  summary?: string | null;
  validFrom?: string;
  validTo?: string;
  priority?: number;
  coverage?: number;
  hasProhibitedContents?: number;
  lastTestedAt?: string | null;
  overridesFlag?: number;
  trackingLink?: string | null;
  promoLink?: string | null;
  domains?: string[];
  countries?: string[];
  metadata?: IKindredMetadataUpdate;
}

export interface IKindredBrandUpdate {
  name?: string;
  countryCode?: string | null;
  logo?: string | null;
  topBrandCountries?: string[];
}

// ── Create DTOs (write operations) ──────────────────────────

export interface IKindredBrandCreate {
  name: string;
  countryCode?: string | null;
  logo?: string | null;
}

export interface IKindredDealCreate {
  name: string;
  brandId: string;
  publishedStatus?: number;
  source?: string;
  description?: string;
  shopLink?: string;
  startDate: string;
  endDate: string;
  suspended?: boolean;
  featured?: boolean;
  exclusive?: boolean;
  domains?: string[];
  countries?: string[];
}

export interface IKindredCouponCreate {
  code: string;
  brandId: string;
  discount?: number;
  summary?: string | null;
  validFrom: string;
  validTo: string;
  publishedStatus?: number;
  priority?: number;
  coverage?: number;
  source?: string;
  domains?: string[];
  countries?: string[];
  metadata?: IKindredMetadataUpdate;
}

export interface IKindredOfferCreate {
  brandId: string;
  summary?: string | null;
  validFrom: string;
  validTo: string;
  publishedStatus?: number;
  priority?: number;
  source?: string;
  trackingLink?: string | null;
  promoLink?: string | null;
  domains?: string[];
  countries?: string[];
  metadata?: IKindredMetadataUpdate;
}

// ── Coupon Targeting ──────────────────────────────────────

export interface IKindredCouponTargetingListItem {
  id: string;
  code: string;
  brandId: string;
  brandName: string;
  summary: string | null;
  validTo: string;
  targetCount: number;
}

export interface IKindredCouponTargetingListResponse {
  results: IKindredCouponTargetingListItem[];
}

export interface IKindredCouponTargetingRecord {
  id: string;
  code: string;
  brandId: string;
  coverage: number;
  merchantDomain: string | null;
  advertiserId: string | null;
  categoryPath: string | null;
  manufacturerName: string | null;
  manufacturerCanonical: string | null;
  manufacturerId: string | null;
  productMerchantProductId: string | null;
  productSku: string | null;
  productEan: string | null;
  productGtin: string | null;
  productUpc: string | null;
  productMpn: string | null;
  productName: string | null;
  productUrl: string | null;
  canonicalKey: string | null;
  directKind: number | null;
  directCosmosId: string | null;
  directPartitionKey: string | null;
  directNetwork: string | null;
  directPinnedBy: string | null;
  directPinnedAt: string | null;
  conditions: ITargetingConditions | null;
  deactivatedAt: string | null;
  deactivationReason: string | null;
  deactivatedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface IKindredCouponTargetingResponse {
  couponId: string;
  code: string;
  summary: string | null;
  brandId: string;
  brandName: string;
  coverage: number;
  priority: number;
  metadata: IKindredCouponMetadata | null;
  targets: IKindredCouponTargetingRecord[];
}

export interface IKindredCouponTargetingUpsert {
  coverage: number;
  merchantDomain?: string | null;
  advertiserId?: string | null;
  categoryPath?: string | null;
  manufacturerName?: string | null;
  manufacturerCanonical?: string | null;
  manufacturerId?: string | null;
  productMerchantProductId?: string | null;
  productSku?: string | null;
  productEan?: string | null;
  productGtin?: string | null;
  productUpc?: string | null;
  productMpn?: string | null;
  productName?: string | null;
  productUrl?: string | null;
  canonicalKey?: string | null;
  directKind?: number | null;
  directCosmosId?: string | null;
  directPartitionKey?: string | null;
  directNetwork?: string | null;
  directPinnedBy?: string | null;
  directPinnedAt?: string | null;
  conditions?: ITargetingConditions | null;
  deactivatedAt?: string | null;
  deactivationReason?: string | null;
  deactivatedBy?: string | null;
}

// ── Override Flag Enums (bitwise) ───────────────────────────

export enum CouponOverridesFlag {
  None = 0,
  Countries = 1,
  Dates = 2,
  Summary = 4,
  ProhibitedContents = 8,
  Domains = 16,
  Priority = 32,
}

export enum CampaignOverridesFlag {
  None = 0,
  ShippingCountries = 1,
  Domains = 2,
  TotalCommissionPercent = 4,
  Suspended = 8,
  ShopLink = 16,
}

export enum OfferOverridesFlag {
  None = 0,
  Countries = 1,
  Dates = 2,
  Summary = 4,
  ProhibitedContents = 8,
  Domains = 16,
  Priority = 32,
}

// ── Non-Agentic Brands ──────────────────────────────────────

export interface IKindredNonAgenticRow {
  id: string;
  brandId: string;
  brandName: string;
  code: string;
  summary: string | null;
  domains: string | null;
  inferredDomain: string | null;
  countryCodes: string | null;
  source: string;
  validFrom: string;
  validTo: string;
  isAgentic: boolean;
}

export interface IKindredNonAgenticFilter {
  brandName?: string;
  countryCode?: string;
  domain?: string;
  source?: string;
  includeAgentic?: boolean;
}

export interface IKindredNonAgenticResponse {
  results: IKindredNonAgenticRow[];
  pagination: IKindredPagination;
}

export interface IKindredNonAgenticSnapshotListItem {
  id: string;
  snapshotDate: string;
  createdAt: string;
  total: number;
  totalNonAgentic: number;
}

export interface IKindredNonAgenticSnapshotResponse {
  id: string;
  snapshotDate: string;
  createdAt: string;
  total: number;
  totalNonAgentic: number;
  results: IKindredNonAgenticRow[];
  pagination: IKindredPagination;
}

// ── Samsung Check ───────────────────────────────────────────

export interface ISamsungCheckRequest {
  domain: string;
  countryCode: string;
}

export interface ISamsungCheckResponse {
  domain: string;
  countryCode: string;
  hasDeal: boolean;
  dealInfo: {
    id: string;
    storeId: string;
    storeName: string;
    cashback: number;
    cashbackType: string;
    currency: string | null;
    logo: string | null;
    startDate: string;
    endDate: string;
  } | null;
  automaticFlow: {
    eligible: boolean;
    sitewideCouponCount: number;
    templateCount: number;
    skuTemplateCount: number;
  };
  manualFlow: {
    eligible: boolean;
    couponCount: number;
    offerCount: number;
    listType: "coupons_only" | "offers_only" | "both" | "none";
    coupons: Array<{
      id: string;
      code: string;
      priority: number;
      isSiteWide: boolean;
      role: number;
      summary: string;
      brandName: string | null;
      brandId: string | null;
    }>;
    offers: Array<Record<string, unknown>>;
  };
  showForSamsung: boolean;
}

// ── Bulk Coupon Import ──────────────────────────────────────

export const BULK_IMPORT_ALLOWED_FIELDS = ["coverage"] as const;
export type BulkImportField = (typeof BULK_IMPORT_ALLOWED_FIELDS)[number];

export interface IBulkCouponImportValidatedRow {
  id: string;
  coverage: number;
  code: string;
  brandName: string;
  currentCoverage: number;
  hasMetadata: boolean;
  couponHash: string | null;
  isActive: boolean;
}

export interface IBulkCouponImportErrorRow {
  id: string;
  coverage: string;
  error: string;
}

export interface IBulkCouponImportStageResponse {
  importId: string;
  validCount: number;
  invalidCount: number;
  noChangeCount: number;
  missingMetadataCount: number;
  valid: IBulkCouponImportValidatedRow[];
  invalid: IBulkCouponImportErrorRow[];
  noChange: IBulkCouponImportValidatedRow[];
  missingMetadataIds: string[];
}

export interface IBulkCouponImportConfirmResult {
  successCount: number;
  errorCount: number;
  errors: IBulkCouponImportErrorRow[];
}

// ── Same-code propagation ───────────────────────────────────
// A brand often carries the identical code on several domains/geos as separate
// coupon rows. These let one edit be applied across all of them, but only
// after the tester has seen exactly which rows it will touch.

/** Every other coupon in the SAME brand carrying the identical code. */
export interface IKindredCouponSiblingsResponse {
  code: string;
  brandId: string;
  brandName: string | null;
  siblings: IKindredCouponSummary[];
}

export interface IKindredCouponApplyToSiblingsRequest {
  /** The coupon fields to copy across. Identity fields (code, brandId,
   *  domains, countries) are rejected — they are what makes a row distinct. */
  data: Record<string, unknown>;
  comment: string;
  /** Explicitly chosen from the confirmation list; anything that isn't a real
   *  sibling is rejected server-side rather than silently skipped. */
  targetIds: string[];
}

export interface IKindredCouponApplyToSiblingsResult {
  results: { id: string; code: string; success: boolean; error?: string }[];
  successCount: number;
  failureCount: number;
}
